import { store, store$ } from '@redux/store';
import { distinctUntilChanged, filter, map, merge, skip, tap } from 'rxjs';
import {
  selectPage,
  selectPageSize,
  onArticleCountResultsLoaded$,
  articleSearchResultsActions
} from 'features/articleSearchResultsFacets';
import createPagination from 'features/pagination';
import {
  routingSelectors,
  buildNewUrl,
  getPaging
} from 'features/routing';

const paginationSelector = '[data-component="blog-author-pagination"]';
const previousButtonSelector = `${paginationSelector} [data-prev-button]`;
const nextButtonSelector = `${paginationSelector} [data-next-button]`;
const optionListSelector = `${paginationSelector} [data-option-list]`;
const pageSizeSelector = `${paginationSelector} [data-page-size]`;
const pageTargetSelector = `[data-page-link]`;
const pageSizeDefaultValue = 40;
const countResultsThreshold = 20;

/**
 * Dispatches a Redux action to update the current page number.
 * @param {number} newPageNumber
 * @returns {*}
 */
const updatePageNumber = (newPageNumber) =>
  store.dispatch(
    articleSearchResultsActions.updatePageParameter(newPageNumber)
  );

/**
 * Returns the closest clickable pagination element from the event target.
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
const getLinkableElement = (event) =>
  event.target.closest(pageTargetSelector);

/**
 * Extracts the total result count from the pagination container dataset.
 * @param {HTMLElement | undefined | null} paginationContainer
 * @returns {number}
 */
const getCountResults = () => {
  const paginationContainer = document.querySelector(paginationSelector);
  return Number(paginationContainer?.dataset?.countResults || 0);
};

/**
 * Determines whether pagination should be displayed.
 * @param {number} countResults
 * @returns {boolean}
 */
const shouldShowPagination = (countResults) => countResults > countResultsThreshold;

/**
 * Pagination component instance.
 * Responsible for rendering and handling pagination interactions.
 */
const pagination = createPagination({
  selectors: {
    previousButton: previousButtonSelector,
    nextButton: nextButtonSelector,
    optionList: optionListSelector,
    pageTarget: pageTargetSelector,
    pageSize: pageSizeSelector
  },
  callbacks: {
    onPageSelect: (event) => {
      const linkableElement = getLinkableElement(event);
      if (linkableElement) {
        event.preventDefault();
      }

      const page = Number(linkableElement?.dataset?.pageLink || 0);
      return updatePageNumber(page);
    },
    onNext: () => {
      const state = store.getState();
      const currentPageNumber = selectPage(state);
      return updatePageNumber(currentPageNumber + 1);
    },
    onPrevious: () => {
      const state = store.getState();
      const currentPageNumber = selectPage(state);
      return updatePageNumber(currentPageNumber - 1);
    },
    onPageSizeChange: (event) => {
      const newPageSize = Number(event.target?.value || pageSizeDefaultValue);
      store.dispatch(articleSearchResultsActions.updatePageSizeParameter(newPageSize));
    }
  },

  /**
   * Builds a URL for a given page number.
   * @param {number} page
   * @returns {string}
   */
  buildPageUrl: (page) => {
    const state = store.getState();
    const pagingSegment = getPaging(page);
    const urlComponents =
      routingSelectors.urlComponentsSelector(state);
    const urlSegments = {
      ...urlComponents,
      urlPaging: pagingSegment
    };

    return buildNewUrl(urlSegments);
  }
});

// Emits when the current page changes.
const onPageChange$ = store$.pipe(
  map(selectPage),
  filter(Boolean),
  distinctUntilChanged(),
  skip(1),
  map(getCountResults)
);

// Emits when the page size changes.
const onPageSizeChange$ = store$.pipe(
  map(selectPageSize),
  filter(Boolean),
  distinctUntilChanged(),
  skip(1),
  map(getCountResults)
);

// Updates the pagination container dataset when new result counts are loaded.
const newCountResults$ = onArticleCountResultsLoaded$.pipe(
  tap((countResults) => {
    const paginationContainer = document.querySelector(paginationSelector);
    if (paginationContainer) {
      paginationContainer.dataset.countResults = countResults;
    }
  })
);

// Emits whenever pagination-related state changes require a refresh.
const onPaginationChange$ = merge(
  onPageChange$,
  onPageSizeChange$,
  newCountResults$
).pipe(
  filter(shouldShowPagination)
);

// Triggers pagination refresh on relevant state changes.
onPaginationChange$.subscribe((countResults) => {
  const state = store.getState();
  pagination.refresh(countResults, selectPageSize(state), selectPage(state));
});

// function to reset the page size component to the first initial value
const resetPageSizeComponent = function(){
  const pageSizeElement = document.querySelector(pageSizeSelector);
  if (pageSizeElement) {
    pageSizeElement.selectedIndex = 0;
  }
};


newCountResults$.subscribe(resetPageSizeComponent);